import api, { sys } from '@/api/axios'
import { defineStore } from 'pinia'

export enum UserRole {
  guest = 'guest',
  user = 'user',
  admin = 'admin',
  controller = 'controller',
}

export interface PendingUser {
  id: number | null
  email: string | null
  phone: string | null
  signup_method: 'email' | 'phone' | null
}

export interface UserProfile {
  id: number
  name: string | null
  surname: string | null
  email: string
  phone: string | null
  birthday: string | null
  city_id: number | null
  city_name: string | null
  is_verified: boolean
  is_phone_verified: boolean
  role: 'user' | 'admin' | 'controller'
}

export interface AuthState {
  isLoggedIn: boolean
  profile: UserProfile | null
  isAuthChecked: boolean
  pendingUser: PendingUser | null
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    isLoggedIn: false,
    profile: null,
    isAuthChecked: false,
    pendingUser: null,
  }),

  getters: {
    isAuthenticated: (s): boolean => s.isLoggedIn,

    // роль как enum (guest если нет профиля)
    userRole: (s): UserRole => {
      const r = s.profile?.role
      if (r === 'admin') return UserRole.admin
      if (r === 'controller') return UserRole.controller
      if (r === 'user') return UserRole.user
      return UserRole.guest
    },

    isAdmin(): boolean {
      return this.userRole === UserRole.admin
    },
    isController(): boolean {
      return this.userRole === UserRole.controller
    },
    isUser(): boolean {
      return this.userRole === UserRole.user
    },
    isGuest(): boolean {
      return this.userRole === UserRole.guest
    },
  },

  actions: {
    // алиас для старого кода
    async fetchUser(): Promise<boolean> {
      return this.checkAuth(true)
    },

    /**
     * Проверка: залогинен ли пользователь.
     */
    async checkAuth(force = false): Promise<boolean> {
      if (!force && this.isAuthChecked) return this.isLoggedIn


      const loadProfile = async () => {
        const { data } = await api.get<UserProfile>('accounts/profile/settings/')
        this.profile = data
        this.isLoggedIn = true
      }
      try {
        await loadProfile()
      } catch(e: any) {
        if(e?.response?.status===401) {
          try {
            await sys.post('api/token/refresh/', {})
            await loadProfile()
          } catch {
            this.profile = null
            this.isLoggedIn = false
          }
        } else {
          this.profile = null
          this.isLoggedIn = false
        }
      } finally {
        this.isAuthChecked = false
      }

      return this.isLoggedIn
    },

    /**
     * Логин: сервер ставит HttpOnly cookies (access/refresh).
     * После логина сразу подтягиваем профиль.
     */
    async login(credentials: { email: string; password: string }): Promise<void> {
      await sys.post('/api/token/', credentials) // cookies ставятся сервером
      this.isAuthChecked = false
      await this.checkAuth(true)
    },

    /**
     * Logout: удаляем cookies и чистим локальный state.
     */
    async logout(): Promise<void> {
      try {
        await sys.post('http://localhost:8000/api/logout/', {})
      } catch {
      } finally {
        this.profile = null
        this.isLoggedIn = false
        this.isAuthChecked = true
      }
    },

    loadPendingUserFromStorage(): void {
      try {
        const raw = localStorage.getItem('pendingUser')
        this.pendingUser = raw ? (JSON.parse(raw) as PendingUser) : null
      } catch {
        this.pendingUser = null
      }
    },

    setPendingUser(u: PendingUser | null): void {
      this.pendingUser = u
      if (u) localStorage.setItem('pendingUser', JSON.stringify(u))
      else localStorage.removeItem('pendingUser')
    },
  },
})
